import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Center,
  Group,
  Pagination,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Select,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { LogoImg } from "../components/LogoImg";
import { api } from "../lib/api";

interface CatalogProduct {
  id: string;
  name: string;
  modelNumber: string | null;
  msrp: string | null;
  isActive: boolean;
  brandName: string;
  brandDomain: string | null;
  productLineName: string;
  series: string | null;
  l1Category: string | null;
  l2Subcategory: string | null;
  l3ProductType: string | null;
}

const formatCurrency = (value: string | null): string => {
  if (value === null) return "-";
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parsed);
};

const columnHelper = createColumnHelper<CatalogProduct>();

const columns = [
  columnHelper.accessor("brandName", {
    header: "Brand",
    cell: (info) => (
      <Group gap={6} wrap="nowrap">
        {info.row.original.brandDomain && (
          <LogoImg domain={info.row.original.brandDomain} size={20} alt={info.getValue()} />
        )}
        <Text size="sm" fw={500}>{info.getValue()}</Text>
      </Group>
    ),
    size: 110,
  }),
  columnHelper.accessor("l1Category", {
    header: "Category",
    cell: (info) => <Text size="sm">{info.getValue() ?? "-"}</Text>,
    size: 140,
  }),
  columnHelper.accessor("l2Subcategory", {
    header: "Subcategory",
    cell: (info) => <Text size="sm">{info.getValue() ?? "-"}</Text>,
    size: 130,
  }),
  columnHelper.accessor("l3ProductType", {
    header: "Product Type",
    cell: (info) => {
      const val = info.getValue();
      if (!val) return <Text size="sm" c="dimmed">-</Text>;
      return <Badge variant="light" color="blue" size="sm">{val}</Badge>;
    },
    size: 140,
  }),
  columnHelper.accessor("productLineName", {
    header: "Product Line",
    cell: (info) => <Text size="sm">{info.getValue()}</Text>,
    size: 160,
  }),
  columnHelper.accessor("name", {
    header: "Product",
    cell: (info) => <Text size="sm" lineClamp={1}>{info.getValue()}</Text>,
    size: 280,
  }),
  columnHelper.accessor("modelNumber", {
    header: "Model",
    cell: (info) => (
      <Text size="xs" ff="monospace" c="dimmed">{info.getValue() ?? "-"}</Text>
    ),
    size: 150,
  }),
  columnHelper.accessor("msrp", {
    header: "MSRP",
    cell: (info) => (
      <Text size="sm" fw={500} ta="right">{formatCurrency(info.getValue())}</Text>
    ),
    sortingFn: (a, b) => {
      const aVal = a.original.msrp ? parseFloat(a.original.msrp) : 0;
      const bVal = b.original.msrp ? parseFloat(b.original.msrp) : 0;
      return aVal - bVal;
    },
    size: 100,
  }),
  columnHelper.accessor("isActive", {
    header: "Status",
    cell: (info) => (
      <Badge color={info.getValue() ? "green" : "gray"} variant="light" size="sm">
        {info.getValue() ? "Active" : "Inactive"}
      </Badge>
    ),
    size: 80,
  }),
];

export function CatalogPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [l1Filter, setL1Filter] = useState<string | null>(null);
  const [l2Filter, setL2Filter] = useState<string | null>(null);
  const [l3Filter, setL3Filter] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<CatalogProduct[]>("/catalog/products")
      .then(({ data }) => setProducts(data))
      .catch((err) => {
        notifications.show({
          title: "Failed to load catalog",
          message: err instanceof Error ? err.message : "Network error",
          color: "red",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  // Pre-filter data based on dropdown selections
  const filteredData = useMemo(() => {
    let data = products;
    if (brandFilter) data = data.filter((p) => p.brandName === brandFilter);
    if (l1Filter) data = data.filter((p) => p.l1Category === l1Filter);
    if (l2Filter) data = data.filter((p) => p.l2Subcategory === l2Filter);
    if (l3Filter) data = data.filter((p) => p.l3ProductType === l3Filter);
    return data;
  }, [products, brandFilter, l1Filter, l2Filter, l3Filter]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [brandFilter, l1Filter, l2Filter, l3Filter, globalFilter]);

  // Cascading filter options — each level's options depend on the level above
  const brandOptions = useMemo(
    () => [...new Set(products.map((p) => p.brandName))].sort(),
    [products],
  );

  const l1Options = useMemo(() => {
    let data = products;
    if (brandFilter) data = data.filter((p) => p.brandName === brandFilter);
    return [...new Set(data.map((p) => p.l1Category).filter(Boolean))].sort() as string[];
  }, [products, brandFilter]);

  const l2Options = useMemo(() => {
    let data = products;
    if (brandFilter) data = data.filter((p) => p.brandName === brandFilter);
    if (l1Filter) data = data.filter((p) => p.l1Category === l1Filter);
    return [...new Set(data.map((p) => p.l2Subcategory).filter(Boolean))].sort() as string[];
  }, [products, brandFilter, l1Filter]);

  const l3Options = useMemo(() => {
    let data = products;
    if (brandFilter) data = data.filter((p) => p.brandName === brandFilter);
    if (l1Filter) data = data.filter((p) => p.l1Category === l1Filter);
    if (l2Filter) data = data.filter((p) => p.l2Subcategory === l2Filter);
    return [...new Set(data.map((p) => p.l3ProductType).filter(Boolean))].sort() as string[];
  }, [products, brandFilter, l1Filter, l2Filter]);

  // Clear child filters when parent changes
  const handleBrandChange = (val: string | null) => {
    setBrandFilter(val);
    setL1Filter(null);
    setL2Filter(null);
    setL3Filter(null);
  };
  const handleL1Change = (val: string | null) => {
    setL1Filter(val);
    setL2Filter(null);
    setL3Filter(null);
  };
  const handleL2Change = (val: string | null) => {
    setL2Filter(val);
    setL3Filter(null);
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting, globalFilter, pagination },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (loading) {
    return (
      <Stack gap="md">
        <Title order={2}>Product Catalog</Title>
        <Skeleton height={500} radius="sm" />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>Product Catalog</Title>
          <Text size="sm" c="dimmed">
            {table.getFilteredRowModel().rows.length} of {products.length} products across {brandOptions.length} brands
          </Text>
        </div>
      </Group>

      <Group gap="sm" wrap="wrap">
        <TextInput
          placeholder="Search products, models..."
          leftSection={<IconSearch size={16} />}
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
          style={{ minWidth: 220 }}
          size="sm"
        />
        <Select
          placeholder="All brands"
          data={brandOptions}
          value={brandFilter}
          onChange={handleBrandChange}
          clearable
          size="sm"
          style={{ minWidth: 130 }}
        />
        <Select
          placeholder="All categories"
          data={l1Options}
          value={l1Filter}
          onChange={handleL1Change}
          clearable
          size="sm"
          style={{ minWidth: 160 }}
        />
        <Select
          placeholder="All subcategories"
          data={l2Options}
          value={l2Filter}
          onChange={handleL2Change}
          clearable
          disabled={l2Options.length === 0}
          size="sm"
          style={{ minWidth: 150 }}
        />
        <Select
          placeholder="All product types"
          data={l3Options}
          value={l3Filter}
          onChange={setL3Filter}
          clearable
          disabled={l3Options.length === 0}
          size="sm"
          style={{ minWidth: 160 }}
        />
      </Group>

      {filteredData.length === 0 ? (
        <Center py={60}>
          <Text c="dimmed">No products match your filters.</Text>
        </Center>
      ) : (
        <>
        <Table.ScrollContainer minWidth={1100}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.Th
                      key={header.id}
                      style={{
                        cursor: header.column.getCanSort() ? "pointer" : undefined,
                        userSelect: "none",
                        width: header.getSize(),
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Group gap={4} wrap="nowrap">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{ asc: " \u2191", desc: " \u2193" }[
                          header.column.getIsSorted() as string
                        ] ?? ""}
                      </Group>
                    </Table.Th>
                  ))}
                </Table.Tr>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {table.getRowModel().rows.map((row) => (
                <Table.Tr
                  key={row.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/history/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        <Group justify="space-between" align="center">
          <Group gap="xs" align="center">
            <Text size="sm" c="dimmed">Rows per page:</Text>
            <Select
              data={["10", "25", "50", "100"]}
              value={String(pagination.pageSize)}
              onChange={(val) => {
                setPagination({ pageIndex: 0, pageSize: Number(val) });
              }}
              size="xs"
              style={{ width: 70 }}
            />
            <Text size="sm" c="dimmed">
              {pagination.pageIndex * pagination.pageSize + 1}–
              {Math.min(
                (pagination.pageIndex + 1) * pagination.pageSize,
                table.getFilteredRowModel().rows.length,
              )}{" "}
              of {table.getFilteredRowModel().rows.length}
            </Text>
          </Group>
          <Pagination
            total={table.getPageCount()}
            value={pagination.pageIndex + 1}
            onChange={(page) => setPagination((prev) => ({ ...prev, pageIndex: page - 1 }))}
            size="sm"
          />
        </Group>
        </>
      )}
    </Stack>
  );
}
